import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, MessageCircle, Instagram, Mail, MapPin, Loader2, CheckCircle2, Bot } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/motion";
import { CONTACT, PILLARS, PLAN_OPTIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-white border border-ink/10 rounded-2xl px-5 py-3.5 text-sm text-ink placeholder:text-slate/70 focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo transition-[box-shadow,border-color] duration-200";

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", business: "", service: searchParams.get("plan") || "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch within one business day.");
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section className="pt-[72px] hero-grid-bg" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-14 sm:pt-28 sm:pb-16">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Get started</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl leading-[1.06]">
              Let's get your business <span className="text-gradient">online</span>.
            </h1>
            <p className="mt-6 text-slate text-base sm:text-lg max-w-2xl leading-relaxed">
              Tell us a little about your business and we'll come back with a free Digital Health Check — what's working, what's missing, and the fastest way to fix it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24" data-testid="contact-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
          <Reveal>
            <div className="bg-white rounded-[2rem] border border-ink/5 shadow-sm p-8 sm:p-10">
              {sent ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <span className="w-16 h-16 rounded-full bg-mint/15 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-mint" />
                  </span>
                  <h2 className="font-display font-bold text-2xl text-ink mb-3">Message received.</h2>
                  <p className="text-slate text-sm max-w-sm mx-auto leading-relaxed">
                    Thanks {form.name.split(" ")[0]} — we've got your enquiry and a confirmation is on its way to your inbox. Expect a reply within one business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-ink mb-2">Your name *</label>
                      <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Citizen" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-ink mb-2">Email *</label>
                      <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="jane@business.com.au" className={inputCls} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-business" className="block text-sm font-medium text-ink mb-2">Business name</label>
                      <input id="contact-business" data-testid="contact-business-input" value={form.business} onChange={set("business")} placeholder="Jane's Bakery" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="contact-service" className="block text-sm font-medium text-ink mb-2">I'm interested in</label>
                      <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                        <option value="">Choose a service...</option>
                        <optgroup label="Packages & Plans">
                          {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </optgroup>
                        {PILLARS.map((p) => (
                          <optgroup key={p.id} label={`${p.name} — ${p.tagline}`}>
                            {p.services.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                          </optgroup>
                        ))}
                        <option value="Full package">A full package</option>
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-ink mb-2">Tell us about your business *</label>
                    <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="What do you do, and what would you like to achieve online?" className={`${inputCls} resize-none`} />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-indigo text-white font-semibold px-8 py-4 rounded-full shadow-[0_12px_32px_rgba(91,95,239,0.35)] hover:bg-ink transition-colors duration-300 disabled:opacity-60"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {sending ? "Sending..." : "Get My Free Digital Health Check"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="space-y-5">
            <Reveal delay={0.1}>
              <div className="bg-ink grain rounded-[2rem] p-8 relative overflow-hidden" data-testid="booking-card">
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-indigo/30 blur-[80px]" />
                <div className="relative z-10">
                  <span className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
                    <CalendarCheck className="w-5 h-5 text-mint" />
                  </span>
                  <h3 className="font-display font-semibold text-white text-lg mb-2">Book a free consultation</h3>
                  <p className="text-white/55 text-sm leading-relaxed mb-6">
                    A relaxed 15-minute call about your business and goals. Our online scheduler is launching soon — for now, send the form and we'll lock in a time.
                  </p>
                  <button
                    type="button"
                    onClick={() => toast.info("Our online scheduler is coming soon — send the form and we'll book you in personally.")}
                    data-testid="booking-placeholder-button"
                    className="inline-flex items-center gap-2 bg-white text-ink font-semibold px-6 py-3 rounded-full hover:bg-mint transition-colors duration-300 text-sm"
                  >
                    <CalendarCheck className="w-4 h-4" /> Scheduler coming soon
                  </button>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="bg-white rounded-[2rem] border border-ink/5 shadow-sm p-8" data-testid="dm-card">
                <h3 className="font-display font-semibold text-ink text-lg mb-5">Prefer to DM?</h3>
                <div className="space-y-3">
                  <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-link" className="flex items-center gap-3.5 bg-cloud rounded-2xl px-5 py-4 hover:bg-mint/15 transition-colors duration-200 group">
                    <span className="w-10 h-10 rounded-xl bg-mint/15 flex items-center justify-center group-hover:bg-mint transition-colors duration-200">
                      <MessageCircle className="w-4 h-4 text-ink group-hover:text-white transition-colors duration-200" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-ink">WhatsApp us</p>
                      <p className="text-xs text-slate">Usually replies within the hour</p>
                    </div>
                  </a>
                  <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="instagram-link" className="flex items-center gap-3.5 bg-cloud rounded-2xl px-5 py-4 hover:bg-indigo/10 transition-colors duration-200 group">
                    <span className="w-10 h-10 rounded-xl bg-indigo/10 flex items-center justify-center group-hover:bg-indigo transition-colors duration-200">
                      <Instagram className="w-4 h-4 text-indigo group-hover:text-white transition-colors duration-200" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-ink">Instagram DM</p>
                      <p className="text-xs text-slate">@avorabusinesssolutions</p>
                    </div>
                  </a>
                  <a href={`mailto:${CONTACT.email}`} data-testid="email-link" className="flex items-center gap-3.5 bg-cloud rounded-2xl px-5 py-4 hover:bg-ink/5 transition-colors duration-200">
                    <span className="w-10 h-10 rounded-xl bg-ink/8 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-ink" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-ink">{CONTACT.email}</p>
                      <p className="text-xs text-slate flex items-center gap-1"><MapPin className="w-3 h-3" /> {CONTACT.location}</p>
                    </div>
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="bg-gradient-to-br from-indigo to-mint rounded-[2rem] p-8 text-white" data-testid="chatbot-promo-card">
                <span className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-5">
                  <Bot className="w-5 h-5 text-white" />
                </span>
                <h3 className="font-display font-semibold text-lg mb-2">Quick question?</h3>
                <p className="text-white/85 text-sm leading-relaxed">
                  Ask Avi, our AI assistant — bottom-right of your screen. It knows our services, pricing and turnaround times inside out.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
